"use client";
import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import styles from "../../styles/AsignacionProgramasStyles/PantallaDeAsignacionProgramaMateria.module.css";
import Sidebar from "../../components/Sidebar";
import HeaderGlobal from "../../components/HeaderGlobal";
import ProgramSelector from "./ProgramSelector";
import SemesterTable from "./SemesterTable";
import SubjectSearch from "./SubjectSearch";
import Toast from "../DocentesScreen/Toast";
import { savePlanEstudio, getPlanesEstudio } from "../../api/planestudio";

function PantallaDeAsignacionProgramaMateria() {
  // Información del programa seleccionado (escuchando evento global despachado en ProgramSelector)
  const [programInfo, setProgramInfo] = useState(null);
  // Lista dinámica de periodos (semestres/tetramestres) con materias asignadas
  const [periods, setPeriods] = useState([]);
  // Estado para loading de carga inicial
  const [loadingPlan, setLoadingPlan] = useState(false);

  // Escuchar selección de programa para regenerar periodos
  useEffect(() => {
    const onProgramSelected = (e) => {
      const detail = e.detail || {};
      setProgramInfo(detail);
    };
    window.addEventListener('program:selected', onProgramSelected);
    return () => window.removeEventListener('program:selected', onProgramSelected);
  }, []);

  // Regenerar periodos y cargar plan existente cuando cambia el programa
  useEffect(() => {
    const duration = programInfo && programInfo.duration ? Number(programInfo.duration) : 0;
    if (!duration || duration < 1) {
      setPeriods([]);
      return;
    }
    
    // Función para cargar el plan existente del backend
    const loadExistingPlan = async () => {
      if (!programInfo || !programInfo.id) return;
      
      setLoadingPlan(true);
      try {
        console.log('[AsignacionProgramas] Cargando plan existente para programa:', programInfo.id);
        const planesData = await getPlanesEstudio(programInfo.id);
        
        // Puede ser array directo o paginado
        const planes = Array.isArray(planesData) ? planesData : (planesData.results || []);
        console.log('[AsignacionProgramas] Planes cargados:', planes.length, planes);
        
        // Crear estructura de periodos con materias agrupadas por periodo_sugerido
        const newPeriods = [];
        for (let i = 1; i <= duration; i++) {
          const materiasDelPeriodo = planes
            .filter(p => Number(p.periodo_sugerido) === i)
            .map(p => {
              // Robusto: obtener ID de múltiples fuentes posibles
              const materiaId = p.id_materia || p.materia?.id || p.materia?.id_materia;
              const materiaNombre = p.materia?.nombre || p.materia?.nombre_materia || '';
              // Manejar créditos: si es null, undefined, string vacío o "—", establecer como null
              const rawCreditos = p.creditos_asignados ?? p.materia?.creditos;
              const materiaCreditos = (rawCreditos === null || rawCreditos === undefined || rawCreditos === '' || rawCreditos === '—') 
                ? null 
                : parseInt(rawCreditos, 10) || null;
              const materiaClave = p.materia?.clave || p.materia?.clave_materia || '';
              
              console.log('[AsignacionProgramas] Materia mapeada:', { id: materiaId, name: materiaNombre });
              
              return {
                id: materiaId,
                name: materiaNombre,
                credits: materiaCreditos,
                code: materiaClave,
              };
            });
          
          newPeriods.push({
            number: i,
            subjects: materiasDelPeriodo
          });
        }
        
        console.log('[AsignacionProgramas] Periodos con materias:', newPeriods);
        setPeriods(newPeriods);
      } catch (error) {
        console.error('[AsignacionProgramas] Error cargando plan existente:', error);
        // Si hay error, crear periodos vacíos
        const emptyPeriods = [];
        for (let i = 1; i <= duration; i++) {
          emptyPeriods.push({ number: i, subjects: [] });
        }
        setPeriods(emptyPeriods);
      } finally {
        setLoadingPlan(false);
      }
    };
    
    loadExistingPlan();
  }, [programInfo]);

  // Agregar materia a un periodo específico (evita duplicados por nombre)
  const handleAddOrMoveSubject = useCallback((targetPeriodNumber, subject) => {
    if (!subject || !targetPeriodNumber) return;
    setPeriods(prev => {
      const sourcePeriodNumber = subject.sourcePeriodNumber;
      return prev.map(p => {
        // Remove from source period if moving
        if (sourcePeriodNumber && p.number === sourcePeriodNumber) {
          return { ...p, subjects: p.subjects.filter(s => (s.id || s.name) !== (subject.id || subject.name)) };
        }
        // Add to target period
        if (p.number === targetPeriodNumber) {
          const already = p.subjects.some(s => (s.id || s.name) === (subject.id || subject.name));
          if (already) return p; // Skip duplicate
          const cleaned = { ...subject };
          delete cleaned.sourcePeriodNumber; // Normalize stored object
          return { ...p, subjects: [...p.subjects, cleaned] };
        }
        return p;
      });
    });
  }, []);

  const handleRemoveSubject = useCallback((periodNumber, subject) => {
    if (!subject || !periodNumber) return;
    setPeriods(prev => prev.map(p => p.number === periodNumber ? { ...p, subjects: p.subjects.filter(s => (s.id || s.name) !== (subject.id || subject.name)) } : p));
  }, []);

  // Emit assignment changed event on any periods change (debounced lightly via microtask)
  useEffect(() => {
    if (!programInfo) return;
    queueMicrotask(() => {
      try {
        window.dispatchEvent(new CustomEvent('assignment:changed', { detail: { programId: programInfo.id, periods } }));
      } catch {}
    });
  }, [periods, programInfo]);

  const periodLabel = (programInfo && programInfo.periodType) ? programInfo.periodType : 'Tetramestre';
  const total = periods.length;
  const firstColumnCount = Math.ceil(total / 2);
  const firstColumn = periods.slice(0, firstColumnCount);
  const secondColumn = periods.slice(firstColumnCount);

  // Estado para loading del guardado
  const [saving, setSaving] = useState(false);

  // Función para guardar el plan de estudios
  const handleSave = useCallback(async () => {
    if (!programInfo || !programInfo.id) {
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'Selecciona un programa académico primero' } }));
      return;
    }
    // Verificar que hay materias asignadas
    const totalMaterias = periods.reduce((acc, p) => acc + p.subjects.length, 0);
    if (totalMaterias === 0) {
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'No hay materias asignadas para guardar' } }));
      return;
    }
    try {
      setSaving(true);
      await savePlanEstudio(programInfo.id, periods);
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Plan de estudios guardado correctamente' } }));
    } catch (e) {
      console.error('Error guardando plan de estudios:', e);
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: `Error al guardar: ${e.message || 'Error desconocido'}` } }));
    } finally {
      setSaving(false);
    }
  }, [programInfo, periods]);

  // Función para descartar cambios (limpiar todas las materias de los períodos)
  const handleDiscard = useCallback(() => {
    setPeriods(prev => prev.map(p => ({ ...p, subjects: [] })));
    window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'info', message: 'Cambios descartados' } }));
  }, []);

  // Verificar si hay períodos para mostrar los botones
  const showActionButtons = periods.length > 0;

  return (
    <div className={styles.mainContainer}>
      <div className={styles.outerLayoutWrapper}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>

        <main className={styles.mainColumn}>
          <div className={styles.mainContent}>
            <HeaderGlobal title="Asignación de Materias a Programa" />
            <div className={styles.contentTwoColumn}>
              <div className={styles.leftPane}>
                <ProgramSelector />

                <div className={styles.semestersGrid}>
                  <div className={styles.semestersContainer}>
                    <div className={styles.semestersRow}>
                      <div className={styles.semesterColumns}>
                        <div className={styles.layoutWrapper}>
                          {firstColumn.map(period => (
                            <div key={period.number} className={styles.semesterColumn}>
                              <SemesterTable
                                semesterNumber={period.number}
                                periodLabel={periodLabel}
                                subjects={period.subjects}
                                onDropSubject={handleAddOrMoveSubject}
                                onRemoveSubject={handleRemoveSubject}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className={styles.semesterColumns}>
                        <div className={styles.layoutWrapper}>
                          {secondColumn.map(period => (
                            <div key={period.number} className={styles.semesterColumn}>
                              <SemesterTable
                                semesterNumber={period.number}
                                periodLabel={periodLabel}
                                subjects={period.subjects}
                                onDropSubject={handleAddOrMoveSubject}
                                onRemoveSubject={handleRemoveSubject}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Botones de acción: Guardar y Descartar Cambios */}
                  {showActionButtons && (
                    <div className={styles.actionButtonsContainer}>
                      <button 
                        className={styles.saveButton}
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? 'Guardando...' : 'Guardar'}
                      </button>
                      <button 
                        className={styles.discardButton}
                        onClick={handleDiscard}
                        disabled={saving}
                      >
                        Descartar cambios
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Panel derecho: Búsqueda de materias */}
              <div className={styles.rightPane}>
                <SubjectSearch />
              </div>
            </div>
          </div>
        </main>
      </div>
      <Toast />
    </div>
  );
}

export default PantallaDeAsignacionProgramaMateria;
